import type { SponsorTip } from "./sponsorTipsConstants";
import {
  ACCOUNT_TEAM_CATEGORY_ORDER,
  CATEGORY_ORDER,
  PROGRAM_LEADER_CATEGORY_ORDER,
  SPONSOR_JOURNEY_BROWSE_KEY,
  SPONSOR_JOURNEY_CATEGORIES,
} from "./sponsorTipsConstants";

export const groupBy = <T>(
  items: T[],
  keyFn: (item: T) => string
): [string, T[]][] => {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    const existing = map.get(key);
    if (existing) existing.push(item);
    else map.set(key, [item]);
  }
  return Array.from(map.entries());
};

export const sortGroupsByOrder = (
  groupEntries: [string, SponsorTip[]][],
  order: string[]
): [string, SponsorTip[]][] => {
  const orderMap = new Map(order.map((id, index) => [id, index]));
  return [...groupEntries].sort(([a], [b]) => {
    const oa = orderMap.has(a)
      ? (orderMap.get(a) ?? order.length)
      : order.length;
    const ob = orderMap.has(b)
      ? (orderMap.get(b) ?? order.length)
      : order.length;
    if (oa !== ob) return oa - ob;
    return String(a).localeCompare(String(b));
  });
};

export const sortCategoryGroups = (
  groupEntries: [string, SponsorTip[]][],
  order: string[] = CATEGORY_ORDER
): [string, SponsorTip[]][] => sortGroupsByOrder(groupEntries, order);

export const sortAccountTeamCategoryGroups = (
  groupEntries: [string, SponsorTip[]][]
): [string, SponsorTip[]][] =>
  sortCategoryGroups(groupEntries, ACCOUNT_TEAM_CATEGORY_ORDER);

export const sortProgramLeaderCategoryGroups = (
  groupEntries: [string, SponsorTip[]][]
): [string, SponsorTip[]][] =>
  sortCategoryGroups(groupEntries, PROGRAM_LEADER_CATEGORY_ORDER);

export const collapseJourneyCategoryGroups = (
  groupEntries: [string, SponsorTip[]][]
): [string, SponsorTip[]][] => {
  const byCategory = new Map(groupEntries);
  const journeyTips: SponsorTip[] = [];

  for (const cat of SPONSOR_JOURNEY_CATEGORIES) {
    const tips = byCategory.get(cat);
    if (tips?.length) journeyTips.push(...tips);
    byCategory.delete(cat);
  }

  const rest = sortCategoryGroups(Array.from(byCategory.entries()));
  if (!journeyTips.length) return rest;
  return [[SPONSOR_JOURNEY_BROWSE_KEY, journeyTips], ...rest];
};

export const groupTipsByLevels = (
  tips: SponsorTip[],
  levelOrder: string[]
): [string, SponsorTip[]][] => {
  const buckets = new Map<string, SponsorTip[]>();
  for (const t of tips) {
    const levels = (t.xfactor_levels || []).includes("ALL")
      ? ["ALL"]
      : t.xfactor_levels || [];
    for (const lvl of levels.length ? levels : ["Unspecified"]) {
      const existing = buckets.get(lvl);
      if (existing) existing.push(t);
      else buckets.set(lvl, [t]);
    }
  }
  return sortGroupsByOrder(Array.from(buckets.entries()), levelOrder);
};

export const groupTipsBySeniority = (
  tips: SponsorTip[],
  seniorityOrder: string[]
): [string, SponsorTip[]][] => {
  const buckets = new Map<string, SponsorTip[]>();
  for (const t of tips) {
    const sen = (t.seniority || []).includes("ALL")
      ? ["ALL"]
      : t.seniority || [];
    for (const s of sen.length ? sen : ["Unspecified"]) {
      const existing = buckets.get(s);
      if (existing) existing.push(t);
      else buckets.set(s, [t]);
    }
  }
  return sortGroupsByOrder(Array.from(buckets.entries()), seniorityOrder);
};

export const groupTipsByRoleLens = (
  tips: SponsorTip[],
  roleOrder: string[]
): [string, SponsorTip[]][] => {
  const buckets = new Map<string, SponsorTip[]>();
  for (const t of tips) {
    const role = t.role_lens || "Both";
    const existing = buckets.get(role);
    if (existing) existing.push(t);
    else buckets.set(role, [t]);
  }
  return sortGroupsByOrder(Array.from(buckets.entries()), roleOrder);
};

export const browseTipsGridClass = (count: number): string => {
  if (count <= 1) return "grid grid-cols-1 gap-4 max-w-lg";
  if (count === 2) return "grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl";
  return "grid gap-4 sm:grid-cols-2 xl:grid-cols-3";
};
